import { forwardRef, useId, type ReactNode, type TextareaHTMLAttributes } from 'react';
import { cn } from './cn';
import { Field, fieldControlClassName, fieldDescribedBy } from './Field';

/**
 * Token-first multi-line input (ADR-015 amendment 2026-07-07). Mirrors `Input` but
 * for `<textarea>`: shared control surface, vertical resize only, optional `Field`
 * labelling with aria wiring.
 */
export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: ReactNode;
  description?: ReactNode;
  error?: ReactNode;
  containerClassName?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  {
    id,
    label,
    description,
    error,
    required,
    className,
    containerClassName,
    'aria-describedby': ariaDescribedBy,
    ...props
  },
  ref,
) {
  const generatedId = useId();
  const controlId = id ?? generatedId;

  const control = (
    <textarea
      ref={ref}
      id={controlId}
      required={required}
      aria-invalid={error ? true : props['aria-invalid']}
      aria-describedby={fieldDescribedBy(controlId, {
        description: label ? description : undefined,
        error: label ? error : undefined,
        extra: ariaDescribedBy,
      })}
      className={cn(fieldControlClassName, 'resize-y', error && 'border-danger', className)}
      {...props}
    />
  );

  if (!label) {
    return control;
  }

  return (
    <Field
      htmlFor={controlId}
      label={label}
      description={description}
      error={error}
      required={required}
      className={containerClassName}
    >
      {control}
    </Field>
  );
});
